import * as React from 'react';
import { createContext, useState } from 'react';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { type FetchResult } from '@apollo/client';
import {
  useLogout,
  useLogin,
  useResetPassword,
  useRequestResetPassword,
} from './graphql';
import {
  type Captcha,
  type AuthContextType,
  type User,
  TokenType,
} from './types';

import { handleRoleNavigation, Loading } from '../../components';

export const AuthContext: React.Context<AuthContextType> =
  createContext<AuthContextType>({} as AuthContextType);

export const AuthContextProvider = ({
  children,
}: {
  children: any;
}): React.ReactNode => {
  const logingql = useLogin();
  const logoutgql = useLogout();
  const requestResetPassword = useRequestResetPassword();
  const resetPasswordGql = useResetPassword();
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(() => {
    const storagedTokens = localStorage.getItem('tokens') ?? '';
    const storagedDlgTokens = localStorage.getItem('delegateToken') ?? '';
    if (storagedTokens !== '') {
      let delegateId: string = '';

      const user: User = jwt_decode(storagedTokens);
      const { sub, delegation = [], ...others } = user;

      if (storagedDlgTokens !== '') {
        const delegateInfo = atob(storagedDlgTokens).split(',');
        delegateId = delegateInfo[0]; // delegateInfo[1]  stands for the dept of delegator
      }

      // for safety reason, check the validation first
      const validateToken = (delegation ?? []).some(
        delegator => delegator?.netId === delegateId,
      );

      const delegateMode = storagedDlgTokens !== '' && validateToken;

      const setUsr: User = {
        sub: delegateMode ? delegateId : sub,
        delegation,
        ...others,
      };

      // currentRole is just for academic staff
      if ((setUsr?.role ?? [])?.includes('applicant'))
        localStorage.removeItem('currentRole');

      return setUsr;
    }

    return null;
  });

  const navigate = useNavigate();

  const role = localStorage.getItem('currentRole');

  const setCurrentRole = (role: string | null): void => {
    if (typeof role === 'string') {
      localStorage.setItem('currentRole', role);
    } else {
      localStorage.removeItem('currentRole');
    }
  };

  const login = async (
    username: string,
    password: string,
    captcha?: Captcha,
  ): Promise<void> => {
    await handleLoading(async () => {
      const res: FetchResult = await logingql({
        variables: { username, password, captcha },
      });
      const loginToken = res?.data?.logIn?.access_token ?? '';
      localStorage.setItem('tokens', loginToken);
      const user: User = jwt_decode(loginToken);
      setUser(user);
    });

    navigate('/applications');
  };

  const logout = async (): Promise<void> => {
    const isStaff = user?.tokenType === TokenType.INTERNAL;
    setPageLoading(true);
    await logoutgql();

    clearAllStorage();
    setPageLoading(false);

    const backTo = isStaff ? '/internal/login' : '/login';
    navigate(backTo);
    if (isStaff) {
      // jump to the external log-off page
      // TODO enhancement: jump back to the homepage of e-admission
      const href =
        process.env.REACT_APP_SSO_LOGOUT_ENDPOINT ??
        'https://adfs-uat.polyu.edu.hk/adfs/oauth2/logout';
      window.location.href = href;
    }

    document.cookie =
      'username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  };

  const ssoLogin = (): void => {
    window.location.href = process.env.REACT_APP_SSO_LOGIN_ENDPOINT ?? '/login';
  };

  const handleNavigation = (role: string) => () => {
    setCurrentRole(role);
    handleRoleNavigation(role);
  };

  const getSSOUser = async (): Promise<void> => {
    // GET from `/user` endpoint
    setPageLoading(true);
    await axios
      .get('/user', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(res => {
        const tokens = res.data;
        window.localStorage.setItem('tokens', tokens);

        let user: User | null = null;

        try {
          user = jwt_decode(tokens);
        } catch (error) {
          console.error('Error: ', error);
        }

        setPageLoading(false);
        setUser(user);
        const firstRole = user?.role?.[0];
        if (firstRole !== undefined) handleNavigation(firstRole);
      });
  };
  const clearAllStorage = (): void => {
    setUser(null);
    setCurrentRole(null);
    localStorage.clear();
  };

  const isTokenValid = (): boolean => {
    const token = localStorage.getItem('tokens') ?? '';

    // check token existing
    if (!token) {
      clearAllStorage();
      return false;
    }

    // check token expire
    const { exp }: User = jwt_decode(token);

    if (!exp || exp * 1000 <= Date.now()) {
      clearAllStorage();

      return false;
    }

    return true;
  };

  const handleLoading = async (
    callback: () => Promise<void>,
  ): Promise<void> => {
    setPageLoading(true);
    try {
      await callback();
    } finally {
      setPageLoading(false);
    }
  };

  const resetPassword = async (
    email: string,
    password: string,
    token: string,
  ): Promise<any> => {
    const { data } = await resetPasswordGql({
      variables: {
        input: {
          email,
          password,
          token,
        },
      },
    });
    return data;
  };

  const getToken = (): string => {
    const token = localStorage.getItem('tokens') ?? '';
    return token;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        pageLoading,
        setUser,
        role,
        setCurrentRole,
        login,
        ssoLogin,
        getSSOUser,
        logout,
        isTokenValid,
        requestResetPassword,
        resetPassword,
        getToken,
        handleLoading,
      }}
    >
      {pageLoading && <Loading />}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  return React.useContext(AuthContext);
};
